<template>
  <div
    class="cg-select"
    :style="styleVariables"
  >
    <label v-if="showLabel" :for="elementId" @click="onFocus">
      <!-- @slot Adds select label. -->
      <slot name="label" />
    </label>

    <div
      class="cg-select__wrapper"
      :class="{ 'cg-select__wrapper--disabled': disabled }"
      ref="wrapper"
    >
      <div
        class="cg-select__content"
        :class="{
          'cg-select__content--focused': showMenu,
          'cg-select__content--filter': showFilterStyles,
        }"
        :aria-invalid="hasError"
        aria-describedby="errmsg"
        :aria-expanded="showMenu ? 'true' : 'false'"
        aria-haspopup="listbox"
        :id="elementId"
        :aria-owns="`list-${elementId}`"
        @click="onContentClick"
      >
        <template v-if="filterable && (isFocused || showMenu)">
          <input
            v-model="filterValue"
            type="text"
            :placeholder="placeholder"
            @focus="onFocus"
            @blur="onBlur"
          >
        </template>

        <template v-else-if="(!isFocused && !modelValue) || !filterable && !modelValue">
          <div class="cg-select__placeholder">
            {{ placeholder }}
          </div>
        </template>

        <template v-else>
          <div class="cg-select__selection">
            <!-- @slot Define a custom selection appearance -->
            <slot name="selection" v-bind="{ ...selectedItem }">
              {{ selectedItem.text }}
            </slot>
          </div>
        </template>

        <div class="cg-select__icon">
          <cg-icon-select-chevron :width="16" :height="16" />
        </div>
      </div>

      <div
        v-show="showMenu"
        class="cg-select__menu"
        role="listbox"
        ref="selectMenu"
        tabindex="-1"
        :id="`list-${elementId}`"
        :class="{
          'cg-select__menu--top': menuProps.top,
          'cg-select__menu--bottom': menuProps.bottom,
          'cg-select__menu--align-left': menuProps.alignLeft,
          'cg-select__menu--align-right': menuProps.alignRight,
        }"
      >
        <template v-if="loading">
          <!-- @slot Defines content for when **loading** is true -->
          <slot name="loading">
            <cg-loader style="padding: 10px 0" />
          </slot>
        </template>

        <template v-else>
          <template v-if="filteredItems.length">
            <template v-if="showPrependItem">
              <!-- @slot Adds an item before menu content -->
              <slot name="prepend-item" />
            </template>

            <div
              v-for="(item, index) in filteredItems"
              class="cg-select__menu-item"
              :class="{
                'cg-select__menu-item--disabled': isItemDisabled(item),
                'cg-select__menu-item--active': isItemSelected(item)
              }"
              :key="`list-item-${elementId}-${index}`"
              :id="`list-item-${elementId}-${index}`"
              role="option"
              tabindex="0"
              @click="onItemClick(item)"
              @keydown.enter="onItemClick(item)"
              @keydown.space="onItemClick(item)"
            >
              {{ item.text }}
            </div>

            <template v-if="showAppendItem">
              <!-- @slot Adds an item after menu content -->
              <slot name="append-item" />
            </template>
          </template>

          <template v-else>
            <div class="cg-select__no-data">
              <!-- @slot Defines content for when no items are provided -->
              <slot name="no-data">
                No data
              </slot>
            </div>
          </template>
        </template>
      </div>
    </div>

    <div v-if="showDetails">
      <div v-if="showHintMessage" class="cg-select__hint-message">
        <!-- @slot Adds an item with hint styles. -->
        <slot name="hint-message" />
      </div>
      <div v-if="showErrorMessage" class="cg-select__error-message" id="errmsg">
        <!-- @slot Adds an item with error styles. -->
        <slot name="error-message">
          {{ getFirstErrorMessage }}
        </slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import type { SelectItem } from '../../types';
import ElementId from '../../mixins/elementId';
import ValidationMethod from '@corporategift/design-system/validations/ValidationMethod';

// @ts-ignore
import { CgIconSelectChevron } from '../../icons'
import CgLoader from '../CgLoader';

// TODO handle arrows events
// TODO add transition

type ISelectSlots = {
  'hint-message'?: unknown;
  'error-message'?: unknown;
  'label'?: unknown;
  'selection'?: unknown;
  'no-data'?: unknown;
  'loading'?: unknown;
  'append-item'?: unknown;
  'prepend-item'?: unknown;
  // items??
}

export default ElementId.extend({
  name: 'CgSelect',
  components: {
    CgIconSelectChevron,
    CgLoader,
  },
  props: {
    /**
     * The input’s value
     * @model
     */
    value: {
      required: false,
      default: null,
    },
    /**
     * The items that will be displayed - can be an array of strings or object
     */
    items: {
      type: Array as PropType<Array<string> | Array<SelectItem>>,
      required: true,
    },
    /**
     * Sets the DOM id on the component
     */
    id: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Disables the input
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Adds loading inside menu
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Hides hint and validation errors.
     */
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Sets the height of the select
     */
    height: {
      type: Number,
      required: false,
      default: 40,
    },
    /**
     * Sets the max-width of the select
     */
    width: {
      type: Number,
      required: false,
      default: null,
    },
    /**
     * Sets the max-height of the menu
     */
    menuHeight: {
      type: Number,
      required: false,
      default: 300,
    },
    /**
     * Adds active styles when value is not empty
     */
    filter: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Sets the select placeholder text
     */
    placeholder: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Array of functions
     * @see list of available validations
     */
    validation: {
      type: Array as PropType<Array<ValidationMethod>>,
      required: false,
      default: () => ([]),
    },
    /**
     * Shows input inside select when select is active and replaces current select value
     */
    filterable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Align menu to **left** or **right** - it's useful when menu can be wider than the input and overflow it
     */
    align: {
      type: String as PropType<'left' | 'right'>,
      required: false,
      default: 'left',
    },
    /**
     * Sets the select menu to be render always on top
     */
    top: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Sets the select menu to be render always on bottom
     */
    bottom: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Puts the input in a manual error state
     */
    error: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    lazyValue: null as any,
    filterValue: null as string | null,
    inputIsValid: true,
    isFocused: false,
    showMenu: false,
    errorMessages: [] as Array<string>,
    menuProps: {
      top: false,
      bottom: false,
      alignLeft: false,
      alignRight: false,
    },
  }),
  computed: {
    modelValue: {
      get(): string | number | null { return this.lazyValue; },
      set(val: string | number | object | null) {
        this.lazyValue = val;
        /**
         * Emitted when the v-model is changed.
         * 
         * @property {any} value new value
         */
        this.$emit('input', this.lazyValue);
      }
    },
    styleVariables(): Object {
      const { height, width, menuHeight } = this;

      return {
        '--select-height': `${height}px`,
        '--select-width': width ? `${width}px` : undefined,
        '--select-menu-height': menuHeight ? `${menuHeight}px` : undefined,
      };
    },
    getFirstErrorMessage(): string | null {
      const { errorMessages } = this

      return errorMessages.length > 0 ? errorMessages[0] : null;
    },
    parsedItems (): Array<SelectItem> {
      const { items } = this;

      if (!items.length) { return [] }

      if (typeof items[0] === 'string') {
        return (items as Array<string>).map<SelectItem>((item) => ({
          text: item,
          value: item,
          disabled: false,
        }))
      }
      
      return items as Array<SelectItem>;
    },
    filteredItems (): Array<SelectItem> {
      const { parsedItems, filterValue } = this;
      return parsedItems.filter((selectItem) => {
        const itemText = selectItem.text.toLowerCase();
        const filteredText = (filterValue || '').toLowerCase();

        return itemText.includes(filteredText)
      })
    },
    selectedItem (): SelectItem | undefined {
      const { parsedItems, lazyValue } = this
      return parsedItems.find((item) => item.value === lazyValue);
    },
    hasInternalError (): boolean {
      return this.errorMessages.length > 0 || !this.inputIsValid;
    },
    hasError (): boolean {
      return this.hasInternalError || this.error;
    },
    slots(): ISelectSlots {
      return this.$slots;
    },
    showLabel(): boolean {
      return !!this.slots?.label;
    },
    showAppendItem(): boolean {
      return !!this.slots?.['append-item'];
    },
    showPrependItem(): boolean {
      return !!this.slots?.['prepend-item'];
    },
    showDetails(): boolean {
      const { slots, errorMessages, hideDetails } = this;

      if (hideDetails) return false;

      return !!(slots?.['hint-message'] || slots?.['error-message'] || errorMessages.length);
    },
    showHintMessage(): boolean {
      return !!this.slots?.['hint-message'];
    },
    showErrorMessage(): boolean {
      return !!(this.slots?.['error-message'] || this.errorMessages.length);
    },
    showFilterStyles(): boolean {
      const { modelValue, filter } = this;

      return !!(filter && modelValue);
    },
  },
  watch: {
    value (val: any) {
      this.lazyValue = val;
    },
    isFocused: function (val: boolean) {
      if (val) {
        this.filterValue = null;

        this.$nextTick(() => {
          const input = this.$el?.querySelector('input');
          input?.focus();
        })
      }
    },
    showMenu: function (val: boolean) {
      if (val) {
        this.setMenuProps();
      } else {
        this.validate();
      }
    },
    parsedItems: {
      immediate: true,
      deep: true,
      handler: function (val: Array<SelectItem>) {
        const { lazyValue } = this;

        if (lazyValue) {
          const availableValues = val.map((item) => item.value);
          const selectedValue = availableValues
            .find((value) => JSON.stringify(value) === JSON.stringify(lazyValue));

          if (!selectedValue) { this.modelValue = null; }
        }
      }
    },
    hasInternalError (val: boolean) {
      /**
       * The `error.sync` event - returns boolean
       * 
       * @property {boolean} val
       */
      this.$emit('update:error', val);
    },
  },
  mounted () {
    this.lazyValue = this.value;

    this.$nextTick(() => {
      document.addEventListener('click', this.clickOutsideHandler)
    });
  },
  beforeDestroy () {
    document.removeEventListener('click', this.clickOutsideHandler)
  },
  methods: {
    clickOutsideHandler (e: MouseEvent): void {
      const { target } = e;
      const { $el } = this;
      const wrapper = $el;

      const targetClassName = (target as Element)?.className;
      const targetId = (target as Element)?.attributes?.getNamedItem('0');
      const parentTargetId = $el?.attributes?.getNamedItem('0');

      const elementsWhiteList = ['cg-select__selection', 'cg-select__placeholder'];

      if (wrapper && !wrapper?.contains(target as Node)) {
        if (!elementsWhiteList.includes(targetClassName) && targetId === parentTargetId) {
          this.showMenu = false;
        }
      }
    },
    setMenuProps (): void {
      const { align, top, bottom } = this

      this.menuProps.alignLeft = align === 'left';
      this.menuProps.alignRight = align === 'right';

      if (top || bottom) {
        this.menuProps.top = top || false;
        this.menuProps.bottom = bottom || false;
      } else {
        const wrapperRect = (this.$refs.wrapper as Element)?.getBoundingClientRect();
        const windowHeight = window?.innerHeight ?? 1000;

        this.$nextTick(() => {
          const { height } = (this.$refs.selectMenu as Element)?.getBoundingClientRect();
          const { top, bottom } = wrapperRect;
          const wrapperBottom = windowHeight - bottom;

          this.menuProps.bottom = (wrapperBottom >= height) || (height > top);
          this.menuProps.top = (height > wrapperBottom) && (top >= height);
        })
      }
    },
    onContentClick (e: FocusEvent): void {
      const { isFocused } = this;

      if (isFocused) {
        this.showMenu = false;
        this.isFocused = false;
      } else {
        this.onFocus(e);
      }
    },
    onFocus (e: FocusEvent): void {
      if (!this.disabled) {
        this.isFocused = true;
        this.showMenu = true;
        /**
         * Emitted when component is focused
         * 
         * @property {FocusEvent} Event
         */
        this.$emit('focus', e);
      }
    },
    onBlur (e: FocusEvent): void {
      this.isFocused = false;
      /**
       * Emitted when the input is blurred
       * 
       * @property {FocusEvent} Event
       */
      this.$emit('blur', e);
    },
    isItemDisabled ({ disabled }: SelectItem): boolean {
      return disabled ?? false;
    },
    isItemSelected ({ value }: SelectItem): boolean {
      const { lazyValue } = this;
      return JSON.stringify(lazyValue) === JSON.stringify(value);
    },
    onItemClick (item: SelectItem): void {
      this.modelValue = item.value;
      this.isFocused = false;
      this.showMenu = false;
    },
    /**
     * Validates input. Returns **true** if successful and **false** if not.
     * @public
     */
    validate (): boolean {
      const value = this.lazyValue;
      const errors: Array<string | boolean> = [];

      (this.validation as Array<ValidationMethod>).forEach((fnc) => {
        const valid = fnc(value);

        if (valid === false || typeof valid === 'string') {
          errors.push(valid || '');
        } else if (typeof valid !== 'boolean') {
          console.error(`Validation should return a string or boolean, received '${typeof valid}' instead`);
        }
      });

      this.inputIsValid = errors.length === 0;
      this.errorMessages = (errors?.filter((item) => ((typeof item === 'string') && item)) ?? []) as Array<string>;

      return !this.hasError;
    },
    /**
     * Resets validation.
     * @public
     */
    resetValidation (): void {
      this.inputIsValid = true;
      this.errorMessages = [];
    },
  },
});
</script>

<style lang="scss" scoped>
@import './styles.scss';
</style>
